import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  CircularProgress,
  Typography,
} from '@mui/material';
import FutureDatetimeTextField from '../../components/forms/FutureDatetimeTextField';
import {
  nowDatetimeLocal,
  clampDatetimeLocal,
  toApiDatetimeFromLocal,
} from '../../utils/datetimeLocal';
import { PAYMENT_STATUS_OPTIONS } from './paymentStatusConstants';
import { getStatusDisplay, getTransactionIdDisplay } from './paymentRowMappers';

const normalizeStatusValue = (row) => {
  const s = row?.status;
  if (typeof s === 'string' && s.trim()) return s.trim().toLowerCase();
  if (s && typeof s === 'object') {
    const v = s.code ?? s.slug ?? s.name;
    if (v) return String(v).trim().toLowerCase();
  }
  const label = getStatusDisplay(row);
  if (label && label !== '—') return String(label).trim().toLowerCase();
  return '';
};

const PaymentStatusChangeDialog = ({ open, payment, onClose, onSubmit, submitting }) => {
  const [status, setStatus] = useState('');
  const [transactionId, setTransactionId] = useState('');
  const [transactionTime, setTransactionTime] = useState('');
  const [minDateTime, setMinDateTime] = useState('');
  const [note, setNote] = useState('');

  useEffect(() => {
    if (!open || !payment) return;
    const current = normalizeStatusValue(payment);
    const allowed = PAYMENT_STATUS_OPTIONS.some((o) => o.value === current);
    const now = nowDatetimeLocal();
    const existingTxnId = payment?.transaction_id;
    setStatus(allowed ? current : '');
    setMinDateTime(now);
    setTransactionTime(now);
    setTransactionId(
      existingTxnId != null && String(existingTxnId).trim() !== ''
        ? String(existingTxnId).trim()
        : ''
    );
    setNote('');
  }, [open, payment]);

  const handleSubmit = () => {
    if (!status || !transactionTime || !transactionId.trim()) return;
    const effectiveTime = clampDatetimeLocal(transactionTime, minDateTime || nowDatetimeLocal());
    onSubmit({
      status,
      transaction_id: transactionId.trim(),
      transaction_time: toApiDatetimeFromLocal(effectiveTime),
      note: note.trim(),
    });
  };

  const showRefundNoteHint = status === 'refunded' || status === 'chargeback';

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 600 }}>Change payment status</DialogTitle>
      <DialogContent sx={{ pt: 1 }}>
        {payment && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Transaction {getTransactionIdDisplay(payment)} · Current status:{' '}
            <strong>{getStatusDisplay(payment)}</strong>
          </Typography>
        )}

        <FormControl fullWidth sx={{ mb: 2 }} required>
          <InputLabel id="payment-status-label">Status</InputLabel>
          <Select
            labelId="payment-status-label"
            label="Status"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            disabled={submitting}
          >
            {PAYMENT_STATUS_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          label="Transaction ID"
          value={transactionId}
          onChange={(e) => setTransactionId(e.target.value)}
          fullWidth
          required
          disabled={submitting}
          sx={{ mb: 2 }}
          placeholder="Enter transaction ID"
        />

        <FutureDatetimeTextField
          label="Transaction time"
          value={transactionTime}
          onChange={setTransactionTime}
          minDatetime={minDateTime}
          fullWidth
          required
          disabled={submitting}
          sx={{ mb: 2 }}
        />

        <TextField
          label="Note"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          fullWidth
          multiline
          minRows={3}
          disabled={submitting}
          placeholder={
            showRefundNoteHint
              ? 'Add a reason for this status change (recommended for refunds)'
              : 'Optional note'
          }
          helperText={
            showRefundNoteHint ? 'You can add a reason when the status is refund or chargeback.' : undefined
          }
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting || !status || !transactionTime || !transactionId.trim()}
          startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : null}
        >
          Update status
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PaymentStatusChangeDialog;
